import httpservice from "./httpservice";

/**
 * Normalize list payloads into { docs, totalDocs, page, limit, totalPages }.
 *
 * Handles mongoose-paginate bodies:
 *   { totalDocs, page, limit, totalPages, data: [...] }
 * and SUCCESS envelopes:
 *   { status: "SUCCESS", data: <paginate object | array> }
 */
function normalizePaginated(res, defaultLimit = 20, defaultPage = 1) {
  if (!res?.data) {
    return { docs: [], totalDocs: 0, limit: defaultLimit, page: defaultPage, totalPages: 1 };
  }

  const envelope = res.data;
  // Axios body is either the paginate object itself, or { status, data: paginate|array }.
  const payload = envelope?.data !== undefined ? envelope.data : envelope;

  let rawList = [];
  let meta = {};

  if (Array.isArray(payload)) {
    // { totalDocs, data: [...] } → payload is the array; meta stays on envelope
    // OR { status, data: [...] } → same
    rawList = payload;
    meta =
      envelope && typeof envelope === "object" && !Array.isArray(envelope)
        ? envelope
        : {};
  } else if (payload && typeof payload === "object") {
    // { status, data: { totalDocs, data: [...] } }
    meta = { ...envelope, ...payload };
    if (Array.isArray(payload.data)) rawList = payload.data;
    else if (Array.isArray(payload.docs)) rawList = payload.docs;
    else if (Array.isArray(payload.items)) rawList = payload.items;
    else if (Array.isArray(payload.addons)) rawList = payload.addons;
    else if (Array.isArray(payload.list)) rawList = payload.list;
    else if (Array.isArray(payload.records)) rawList = payload.records;
  }

  // Prefer pagination fields from the object that owns the list when present
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    (payload.totalDocs != null || payload.page != null)
  ) {
    meta = { ...meta, ...payload };
  } else if (
    envelope &&
    typeof envelope === "object" &&
    !Array.isArray(envelope) &&
    (envelope.totalDocs != null || envelope.page != null)
  ) {
    meta = { ...meta, ...envelope };
  }

  const docs = rawList.map((item) => {
    if (!item) return item;
    const id = item.id || item._id;
    if (!id) return item;
    return { ...item, id: String(id) };
  });

  const totalDocs = Number(meta.totalDocs ?? meta.total ?? meta.count ?? docs.length) || 0;
  const limit = Number(meta.limit ?? defaultLimit) || defaultLimit;
  const page = Number(meta.page ?? defaultPage) || defaultPage;
  const totalPages =
    Number(
      meta.totalPages ??
        meta.pages ??
        Math.max(1, Math.ceil(totalDocs / limit) || 1)
    ) || 1;

  return {
    docs,
    totalDocs,
    limit,
    page,
    totalPages,
    hasNextPage:
      meta.hasNextPage !== undefined ? Boolean(meta.hasNextPage) : page < totalPages,
    hasPrevPage:
      meta.hasPrevPage !== undefined ? Boolean(meta.hasPrevPage) : page > 1,
    pagingCounter: Number(meta.pagingCounter) || (page - 1) * limit + 1,
  };
}

function unwrapData(res) {
  if (!res?.data) return null;
  if (res.data.data !== undefined) return res.data.data;
  return res.data;
}

function patch(url, payload) {
  const patchFn =
    httpservice.patch || httpservice.axios.patch.bind(httpservice.axios);
  return patchFn(url, payload);
}

/**
 * Admin marketplace control-plane client (Part B).
 * Base: /api/v1/admin/cp/marketplace
 */
class MarketplaceService {
  constructor() {
    this.base = "/admin/cp/marketplace";
    this.endpoints = {
      meta: `${this.base}/meta`,
      // Trailing slash on filter list routes — avoids auth-header drop on slash redirect
      items: `${this.base}/items/`,
      itemSingle: `${this.base}/items/single`,
      item: (id) => `${this.base}/items/${id}`,
      itemStatus: (id) => `${this.base}/items/${id}/status`,
      rules: `${this.base}/items/rules`,
      addons: `${this.base}/addons/`,
      addonSingle: `${this.base}/addons/single`,
      addon: (id) => `${this.base}/addons/${id}`,
      grant: `${this.base}/addons/grant`,
      quantity: (id) => `${this.base}/addons/${id}/quantity`,
      revoke: (id) => `${this.base}/addons/${id}/revoke`,
      entitlements: (orgId) =>
        `${this.base}/addons/organizations/${orgId}/entitlements`,
    };
  }

  /** B2 — Static form metadata for building catalogue items. */
  async getMeta() {
    const res = await httpservice.get(this.endpoints.meta);
    return unwrapData(res) || {};
  }

  /**
   * B1 — Browse full catalogue (includes hidden/retired + purchaseCount).
   * Filters: page, limit, kind, rule
   */
  async getItems(query = {}) {
    const params = {};
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.kind && query.kind !== "ALL") params.kind = query.kind;
    if (query.rule) params.rule = query.rule;
    if (query.status && query.status !== "ALL") params.status = query.status;
    if (query.keyword) params.keyword = query.keyword;

    const res = await httpservice.get(this.endpoints.items, { params });
    return normalizePaginated(res, Number(query.limit) || 20, Number(query.page) || 1);
  }

  /** B1 — Inspect a single catalogue item. */
  async getItem(id) {
    if (!id) return null;
    const res = await httpservice.get(this.endpoints.itemSingle, {
      params: { id },
    });
    return unwrapData(res);
  }

  /**
   * B2 / B9 — Capability rules available to attach to items.
   * @param {{ type?: string, enforced?: boolean|string }} query
   */
  async getRules(query = {}) {
    const params = {};
    if (query.type) params.type = query.type;
    if (query.enforced !== undefined && query.enforced !== "") {
      params.enforced = query.enforced;
    }
    const res = await httpservice.get(this.endpoints.rules, { params });
    const data = unwrapData(res);
    return Array.isArray(data) ? data : [];
  }

  /** B2 — Create sellable add-on from rules/meta building blocks. */
  createItem(payload) {
    return httpservice.post(this.endpoints.items, payload);
  }

  /**
   * B3 — Edit item (name, price, bounds, eligibility).
   * Kind / capability / unitValue lock once purchaseCount > 0.
   */
  updateItem(id, payload) {
    return patch(this.endpoints.item(id), payload);
  }

  /**
   * B4 — Lifecycle status: ACTIVE | HIDDEN | RETIRED
   */
  updateItemStatus(id, status) {
    return patch(this.endpoints.itemStatus(id), { status });
  }

  /**
   * B4 — Hard-delete only when never purchased.
   */
  deleteItem(id) {
    return httpservice.delete(this.endpoints.item(id));
  }

  /**
   * B8 — Global audit of purchases and grants.
   * Filters: organizationId, status, source, page, limit
   */
  async getAddons(query = {}) {
    const params = {};
    if (query.organizationId) params.organizationId = query.organizationId;
    if (query.status && query.status !== "ALL") params.status = query.status;
    if (query.source && query.source !== "ALL") params.source = query.source;
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;
    if (query.keyword) params.keyword = query.keyword;

    const res = await httpservice.get(this.endpoints.addons, { params });
    return normalizePaginated(res, Number(query.limit) || 20, Number(query.page) || 1);
  }

  /** B8 — Single purchase/grant record. */
  async getAddon(id) {
    if (!id) return null;
    const res = await httpservice.get(this.endpoints.addonSingle, {
      params: { id },
    });
    return unwrapData(res);
  }

  /**
   * B5 — Org entitlements (why can't this customer do X).
   */
  async getOrganizationEntitlements(orgId, query = {}) {
    if (!orgId) {
      return { docs: [], totalDocs: 0, limit: 20, page: 1, totalPages: 1 };
    }
    const params = {};
    if (query.rule) params.rule = query.rule;
    if (query.page) params.page = query.page;
    if (query.limit) params.limit = query.limit;

    const res = await httpservice.get(this.endpoints.entitlements(orgId), { params });
    return normalizePaginated(res, Number(query.limit) || 20, Number(query.page) || 1);
  }

  /** B6 — Manual free grant (comped/support). Reason required. */
  grantAddon(payload) {
    return httpservice.post(this.endpoints.grant, payload);
  }

  /** B7 — Adjust quantity on capacity grants only. */
  updateAddonQuantity(addonId, payload) {
    return patch(this.endpoints.quantity(addonId), payload);
  }

  /**
   * B7 — Revoke immediately or at period end.
   * @param {{ reason: string, atPeriodEnd?: boolean }} payload
   */
  revokeAddon(addonId, payload) {
    return httpservice.post(this.endpoints.revoke(addonId), payload);
  }
}

const marketplaceService = new MarketplaceService();
export default marketplaceService;
